import { Formik, Form, Field, ErrorMessage } from 'formik'
import React, { memo, useContext, useEffect, useState } from 'react'
import { BsEyeFill } from '@react-icons/all-files/bs/BsEyeFill'
import { BsEyeSlashFill } from '@react-icons/all-files/bs/BsEyeSlashFill'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'

//Internal Imports
import Button from 'components/common/button'
import { Spinner } from 'components/common/spinner'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import {
    clearFormErrors,
    mfaAuth,
    mfaAuthFailed,
    mfaResendCodeFailed,
    resetMFAError,
} from 'modules/authentication/actions'
import { setAgeVerificationRequired } from 'modules/user/actions'
import { util } from 'utils'
import { getCDNURL } from 'network/api/networkSettings'
import Util from 'utils/Util'

const SignInWithRewards = memo(({ signInFromBasket, nextStep, cancelStep, hide, valueRef }) => {
    const { strings } = useContext(LanguageContext)
    const loggedIn = useSelector(state => state.authentication.getIn(['loggedIn']))

    const dispatch = useDispatch()
    const [hidePass, setHidePass] = useState(true)
    const [loader, setLoader] = useState(false)
    const loginError = useSelector(state => state.authentication.getIn(['loginError']))
    const { closePanel, panelOpen } = useContext(LayoutContext)
    const ageVerification = util.ageVerificationNeeded()
    const [skipOpsAtUnmount, setSkipOpsAtUnmount] = useState(false)
    const mfaAuthError = useSelector(state => state.authentication.getIn(['mfaAuthError']))
    const authLoading = useSelector(state => state.authentication.getIn(['authLoading']))

    const handlePass = event => {
        event.preventDefault()
        setHidePass(!hidePass)
    }

    const handleSignIn = ({ userName, password }) => {
        setLoader(true)
        dispatch(clearFormErrors())
        dispatch(mfaAuth({ userName, password }))
    }

    const validationSchema = Yup.object({
        userName: Yup.string().matches(Util.getEmailValidationRegEx(), strings.invalidEmail).email(strings.invalidEmail).required(strings.emailError),
        password: Yup.string().required(strings.passwordError),
    })
    useEffect(() => {
        if (loginError) {
            setLoader(false)
        }
    }, [loginError])

    useEffect(() => {
        dispatch(resetMFAError())
        return () => {
            if (!valueRef.current && signInFromBasket) {
                dispatch(clearFormErrors())
                if (signInFromBasket) {
                    if (panelOpen) {
                        if (loggedIn) {
                            closePanel()
                            if (ageVerification.isNeeded) {
                                dispatch(setAgeVerificationRequired(true))
                            }
                        }
                    } else {
                        hide()
                    }
                }
            }
        }
    }, [])

    const handleResetPwdClick = () => {
        setSkipOpsAtUnmount(true)
        nextStep()
    }

    return (
        <Formik
            initialValues={{ userName: '', password: '' }}
            onSubmit={values => {
                dispatch(mfaAuthFailed(null))
                dispatch(mfaResendCodeFailed(null))
                handleSignIn({ userName: values.userName, password: values.password })
            }}
            validationSchema={validationSchema}
        >
            {({ dirty, isValid, handleSubmit }) => {
                const disabled = !dirty || !isValid
                return (
                    <>
                        {loader ? (
                            <Spinner height={'5rem'} width={'5rem'} />
                        ) : (
                            <Form className={'form'} onSubmit={handleSubmit} autoComplete="off">
                                <div className={'form-header'}>
                                    <p className={'form-header-title'}>{strings.signinButton}</p>
                                    <p className={'form-header-sub'}>
                                        {strings.rewardsTitle1}{' '}
                                        <span>
                                            <img
                                                src={getCDNURL() + 'assets/images/rewards.png'}
                                                alt=""
                                                className="form-header-img"
                                            ></img>
                                        </span>{' '}
                                        {strings.rewardsTitle2}
                                    </p>
                                </div>
                                <div className={'form-input'}>
                                    <div className={'form-input-each input-group'}>
                                        <Field
                                            className={'form-input-each-field'}
                                            type="email"
                                            name="userName"
                                            required
                                            placeholder={strings.SIGN_IN_EMAIL}
                                        />
                                        <div className={'form-input-each-error'}>
                                            &nbsp;
                                            <ErrorMessage name="userName">
                                                {msg => <span>{msg}</span>}
                                            </ErrorMessage>
                                        </div>
                                    </div>

                                    <div className={'form-input-each'}>
                                        <div className={'input-group'}>
                                            <Field
                                                className={'form-input-each-field'}
                                                type={hidePass ? 'password' : 'text'}
                                                name="password"
                                                required
                                                placeholder={strings.SIGN_IN_PASSWORD}
                                            />
                                            <div className={'form-input-each-error'}>
                                                &nbsp;
                                                <ErrorMessage name="password">
                                                    {msg => <span>{msg}</span>}
                                                </ErrorMessage>
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={disabled || authLoading}
                                            style={{ visibility: 'hidden' }}
                                        />
                                        <button
                                            className={'form-input-flipbutton'}
                                            onClick={handlePass}
                                            aria-label="show or hide password"
                                        >
                                            {hidePass ? (
                                                <BsEyeSlashFill
                                                    className={'form-input-flipbutton-icon'}
                                                />
                                            ) : (
                                                <BsEyeFill
                                                    className={'form-input-flipbutton-icon'}
                                                />
                                            )}
                                        </button>
                                        <div className={'form-input-password'}>
                                            <p
                                                className={'form-input-password-text'}
                                                onClick={handleResetPwdClick}
                                            >
                                                {strings.resetPasswordButton}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className={'form-errorMsg'}>
                                    {mfaAuthError?.data?.error_description || ''}
                                </div>
                                <div className={'form-buttons'}>
                                    <button
                                        type="submit"
                                        disabled={disabled || authLoading}
                                        className={`confirm confirm-xl ${
                                            (disabled || authLoading) && 'disabled'
                                        }`}
                                    >
                                        {strings.signinButton}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </>
                )
            }}
        </Formik>
    )
})

export default SignInWithRewards
